/**
 * hc-fixes.js  -  add-on behaviour for sections/header-custom.liquid (~115 KB,
 * never edited directly - the Theme API can only replace whole files and that
 * one is too large to rewrite safely).
 *
 *   1. .hc-tiers  - cart drawer spend-tier bar, recomputed from /cart.js.
 *   2. .hc-upsell - "You may also like" rows that refill themselves and top up
 *                   from the wider catalogue so the block never runs empty.
 *   3. confetti on each tier unlock.
 *
 * Drawer refreshing is NOT done here - see assets/hc-drawer-patch.js.
 * An earlier version rebuilt the drawer by swapping
 * .hc-cart-drawer__header / __body / __footer innerHTML from a section render.
 * That destroyed the close button's listener and blanked the cart (the server
 * markup for __body is empty; the theme builds line items client-side), and
 * losing the drawer's data-open attribute makes everything inside it
 * unclickable. Never swap drawer markup from out here.
 */
(function () {
  'use strict';

  function money(paise) {
    var amount = (paise || 0) / 100;
    var dec = Math.round(amount * 100) % 100 !== 0;
    return 'Rs. ' + new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: dec ? 2 : 0,
      maximumFractionDigits: 2
    }).format(amount);
  }

  function esc(s) {
    var m = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return m[c]; });
  }

  /**
   * Amount the tier ladder is measured against - the PRE-discount subtotal.
   * Shopify evaluates a minimum-purchase automatic discount against the
   * subtotal before the discount, while cart.total_price is the amount after
   * it. Using total_price meant crossing Rs.999 applied 10%, dropped the total
   * to Rs.899.10, and the bar asked for Rs.99.90 more - forever.
   */
  function qualifyingAmount(cart) {
    if (!cart) return 0;
    if (typeof cart.items_subtotal_price === 'number') return cart.items_subtotal_price;
    return (cart.total_price || 0) + (cart.total_discount || 0);
  }

  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .catch(function () { return null; });
  }

  var listeners = [];
  var lastCart = null;

  function onCartChange(fn) {
    listeners.push(fn);
    if (lastCart) { try { fn(lastCart); } catch (e) {} }
  }

  var syncTimer = null;
  function syncCart() {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(function () {
      getCart().then(function (cart) {
        if (!cart) return;
        lastCart = cart;
        listeners.forEach(function (fn) { try { fn(cart); } catch (e) {} });
      });
    }, 150);
  }

  var nativeFetch = window.fetch;
  window.fetch = function () {
    var u = arguments[0];
    if (u && typeof u === 'object' && u.url) u = u.url;
    var isWrite = typeof u === 'string' && /\/cart\/(add|change|update|clear)/.test(u);
    var res = nativeFetch.apply(this, arguments);
    if (isWrite) res.then(function () { syncCart(); }, function () {});
    return res;
  };

  ['cart:refresh', 'cart:updated', 'cart-drawer:open', 'hamper-builder:added']
    .forEach(function (e) { document.addEventListener(e, syncCart); });

  // ------------------------------------------------------------ confetti ---

  var Confetti = (function () {
    var canvas, ctx, parts = [], raf = null;

    function ensure() {
      if (canvas) return;
      canvas = document.createElement('canvas');
      canvas.className = 'hcx-confetti';
      document.body.appendChild(canvas);
      ctx = canvas.getContext('2d');
      size();
      window.addEventListener('resize', size);
    }

    function size() {
      if (!canvas) return;
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = innerWidth * dpr;
      canvas.height = innerHeight * dpr;
      canvas.style.width = innerWidth + 'px';
      canvas.style.height = innerHeight + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    function frame() {
      ctx.clearRect(0, 0, innerWidth, innerHeight);
      for (var i = parts.length - 1; i >= 0; i--) {
        var p = parts[i];
        p.life++;
        p.vy += 0.3; p.vx *= 0.99; p.vy *= 0.99;
        p.x += p.vx; p.y += p.vy; p.rot += p.vr; p.tilt += 0.12;
        var a = p.life > p.ttl * 0.6 ? Math.max(0, 1 - (p.life - p.ttl * 0.6) / (p.ttl * 0.4)) : 1;
        ctx.save();
        ctx.globalAlpha = a;
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.scale(Math.cos(p.tilt), 1);
        ctx.fillStyle = p.c;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
        if (p.life > p.ttl || p.y > innerHeight + 40) parts.splice(i, 1);
      }
      if (parts.length) raf = requestAnimationFrame(frame);
      else { raf = null; ctx.clearRect(0, 0, innerWidth, innerHeight); }
    }

    function spawn(x, y, n, colors, angle, spread, scale) {
      for (var i = 0; i < n; i++) {
        var a = (angle + (Math.random() - 0.5) * spread) * Math.PI / 180;
        var sp = (6 + Math.random() * 8) * scale;
        parts.push({
          x: x, y: y,
          vx: Math.cos(a) * sp, vy: Math.sin(a) * sp,
          w: (5 + Math.random() * 4) * scale, h: (7 + Math.random() * 5) * scale,
          rot: Math.random() * Math.PI, vr: (Math.random() - 0.5) * 0.35,
          c: colors[(Math.random() * colors.length) | 0],
          life: 0, ttl: 80 + Math.random() * 45, tilt: Math.random() * Math.PI
        });
      }
    }

    return {
      burst: function (el, colors) {
        if (window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        ensure();
        var x = innerWidth / 2, y = innerHeight / 2;
        if (el) {
          var r = el.getBoundingClientRect();
          if (r.width || r.height) {
            x = r.left + r.width / 2;
            y = Math.max(60, Math.min(r.top + r.height / 2, innerHeight - 60));
          }
        }
        var narrow = innerWidth < 600;
        var s = narrow ? 0.8 : 1, dx = narrow ? 50 : 80;
        spawn(x - dx, y, narrow ? 20 : 28, colors, -70, 55, s);
        spawn(x + dx, y, narrow ? 20 : 28, colors, -110, 55, s);
        spawn(x, y, narrow ? 14 : 20, colors, -90, 80, s);
        if (!raf) raf = requestAnimationFrame(frame);
      }
    };
  })();

  function themeColors(root) {
    var cs = getComputedStyle(root);
    var pick = function (n, fb) { var v = cs.getPropertyValue(n).trim(); return v || fb; };
    return [
      pick('--hc-tier-bar-fill-start', '#c0392b'),
      pick('--hc-tier-badge-active-bg', '#c0392b'),
      pick('--hc-tier-savings-bg', '#1a1a1a'),
      '#E4B23F', '#F3C57F', '#FFF3D6'
    ];
  }

  // =========================================================== TIER BAR ====

  function initTiers(root) {
    if (root.__hcx) return;
    root.__hcx = true;

    // data-tier-1-threshold maps to dataset['tier-1Threshold'] (the digit stops
    // camel-casing), so dataset.tier1Threshold is undefined - the original bug.
    var tiers = [];
    for (var i = 1; i <= 5; i++) {
      var th = parseInt(root.getAttribute('data-tier-' + i + '-threshold'), 10);
      var pc = parseFloat(root.getAttribute('data-tier-' + i + '-percent'));
      if (th > 0 && pc > 0) tiers.push({ index: i, threshold: th, percent: pc });
    }
    if (!tiers.length) return;
    tiers.sort(function (a, b) { return a.threshold - b.threshold; });

    var progressMsg = root.getAttribute('data-progress-message') ||
      'Spend [[remaining]] more to unlock [[percent]]% off!';
    var completeMsg = root.getAttribute('data-complete-message') ||
      "You've unlocked the maximum [[percent]]% discount!";
    var savingsLabel = root.getAttribute('data-savings-label') || "You're saving";

    var fill = root.querySelector('.hc-tiers__bar-fill');
    var msg = root.querySelector('.hc-tiers__message');
    var save = root.querySelector('.hc-tiers__savings');
    var maxTh = tiers[tiers.length - 1].threshold;

    var writing = false;
    var expected = { width: '', text: '' };
    var amount = 0;
    var celebrated = {};
    var primed = false;

    function paint(total) {
      var current = null, next = null;
      tiers.forEach(function (t) {
        if (total >= t.threshold) current = t;
        else if (!next) next = t;
      });

      var pct = maxTh > 0 ? Math.min((total / maxTh) * 100, 100) : 0;
      var width = pct.toFixed(2) + '%';

      var text;
      if (next) {
        text = progressMsg
          .replace('[[remaining]]', money(next.threshold - total))
          .replace('[[percent]]', next.percent);
      } else if (current) {
        text = completeMsg.replace('[[percent]]', current.percent);
      } else {
        text = progressMsg
          .replace('[[remaining]]', money(tiers[0].threshold))
          .replace('[[percent]]', tiers[0].percent);
      }

      expected.width = width;
      expected.text = text;

      writing = true;
      if (fill) fill.style.width = width;
      if (msg) msg.textContent = text;

      if (save) {
        if (current && total > 0) {
          save.textContent = savingsLabel + ' ' + money(Math.round(total * current.percent / 100));
          save.hidden = false;
        } else {
          save.textContent = '';
          save.hidden = true;
        }
      }

      tiers.forEach(function (t) {
        var el = root.querySelector('.hc-tier[data-hc-tier="' + t.index + '"]');
        var unlocked = total >= t.threshold;
        if (el) {
          var isNext = next && next.index === t.index;
          el.classList.toggle('is-unlocked', unlocked);
          el.classList.toggle('is-complete', unlocked);
          el.classList.toggle('is-active', unlocked || isNext);
          var lock = el.querySelector('.hc-tier__icon--lock');
          var chk = el.querySelector('.hc-tier__icon--check');
          if (lock) lock.style.display = unlocked ? 'none' : '';
          if (chk) chk.style.display = unlocked ? '' : 'none';
        }

        if (unlocked && !celebrated[t.index]) {
          celebrated[t.index] = true;
          if (primed) {
            Confetti.burst(el || root, themeColors(root));
            root.classList.remove('is-celebrating');
            void root.offsetWidth;
            root.classList.add('is-celebrating');
          }
        } else if (!unlocked) {
          celebrated[t.index] = false;
        }
      });

      root.hidden = false;
      root.style.removeProperty('display');
      primed = true;

      setTimeout(function () { writing = false; }, 0);
    }

    function render(cart) {
      amount = qualifyingAmount(cart);
      paint(amount);
    }

    new MutationObserver(function () {
      if (writing) return;
      var badWidth = fill && fill.style.width !== expected.width;
      var badText = msg && msg.textContent.trim() !== expected.text;
      if (badWidth || badText) paint(amount);
    }).observe(root, {
      subtree: true, childList: true, characterData: true,
      attributes: true, attributeFilter: ['style', 'class']
    });

    onCartChange(render);
    getCart().then(function (c) { lastCart = c; render(c); });
  }

  // ======================================================== UPSELL ROWS ====

  var productCache = {};

  function loadProduct(handle) {
    if (productCache[handle]) return Promise.resolve(productCache[handle]);
    return nativeFetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (p) { productCache[handle] = p; return p; })
      .catch(function () { return null; });
  }

  // Module-level so a drawer re-render can rebuild rows without the
  // server-rendered <li>s still being present.
  var upsellPool = [];
  var topUpState = { loaded: false, loading: false };

  function poolHas(key) {
    return upsellPool.some(function (x) { return x.key === key; });
  }

  function harvest(list) {
    Array.prototype.forEach.call(list.querySelectorAll('[data-hc-upsell-item]'), function (li) {
      var a = li.querySelector('.hc-upsell__title');
      var img = li.querySelector('img');
      var price = li.querySelector('.hc-upsell__price-final');
      var was = li.querySelector('.hc-upsell__price-compare');
      var sel = li.querySelector('[data-hc-upsell-variant]');
      var ids = (li.getAttribute('data-variant-ids') || '').split(',').filter(Boolean);
      if (!a || !ids.length) return;

      var key = ids.join(',');
      if (poolHas(key)) return;

      var url = a.getAttribute('href') || '';
      upsellPool.push({
        key: key,
        priority: 0,                      // merchant's chosen collection first
        title: a.textContent.trim(),
        url: url,
        handle: (url.split('/products/')[1] || '').split('?')[0],
        image: img ? img.getAttribute('src') : '',
        now: price ? price.textContent.trim() : '',
        was: was ? was.textContent.trim() : '',
        ids: ids,
        variants: sel && sel.tagName === 'SELECT'
          ? Array.prototype.map.call(sel.options, function (o) {
              return { id: o.value, title: o.textContent.trim(), available: !o.disabled };
            })
          : [{ id: ids[0], title: '', available: true }]
      });
    });
  }

  /**
   * Top the pool up from the wider catalogue.
   *
   * The section only renders however many products the "Upsell limit" setting
   * allows (max 8) from the chosen collection. Once a shopper had added those,
   * the block emptied and disappeared. These extras are appended AFTER the
   * merchant's picks, so the chosen collection is still shown first and the
   * suggestions simply never run out.
   */
  function topUpPool() {
    if (topUpState.loaded || topUpState.loading) return Promise.resolve();
    topUpState.loading = true;

    return nativeFetch('/collections/all/products.json?limit=250')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        (data.products || []).forEach(function (p) {
          if (/build-your-own-hamper/.test(p.handle)) return;   // hidden pack product

          var vs = (p.variants || []).filter(function (v) { return v.available; });
          if (!vs.length) return;

          var ids = vs.map(function (v) { return String(v.id); });
          var key = ids.join(',');
          if (poolHas(key)) return;

          var nowP = Math.round(parseFloat(vs[0].price) * 100);
          if (!nowP) return;
          var wasP = vs[0].compare_at_price ? Math.round(parseFloat(vs[0].compare_at_price) * 100) : 0;

          var im = p.images && p.images[0] ? p.images[0].src : '';
          if (im) im = im.replace(/(\.[a-z]+)(\?|$)/i, '_120x$1$2');

          upsellPool.push({
            key: key,
            priority: 1,                  // fallback, shown after the collection
            title: p.title,
            url: '/products/' + p.handle,
            handle: p.handle,
            image: im,
            now: money(nowP),
            was: wasP > nowP ? money(wasP) : '',
            ids: ids,
            variants: vs.map(function (v) {
              return { id: String(v.id), title: v.title, available: true };
            })
          });
        });
        topUpState.loaded = true;
      })
      .catch(function () {})
      .then(function () { topUpState.loading = false; });
  }

  function initUpsell(root) {
    var list = root.querySelector('.hc-upsell__list');
    if (!list) return;

    harvest(list);
    if (root.__hcx) { root.__hcxRender && root.__hcxRender(); return; }
    root.__hcx = true;

    var VISIBLE = 4;
    var inCart = {};

    list.classList.add('hcx-rail');

    function eligible() {
      return upsellPool
        .filter(function (p) {
          return !p.ids.some(function (id) { return inCart[id]; });
        })
        .sort(function (a, b) { return (a.priority || 0) - (b.priority || 0); });
    }

    function rowHTML(p) {
      var multi = p.variants.length > 1;
      var opts = p.variants.map(function (v) {
        return '<option value="' + esc(v.id) + '"' + (v.available ? '' : ' disabled') + '>' +
          esc(v.title) + (v.available ? '' : ' - Sold out') + '</option>';
      }).join('');

      return '<li class="hc-upsell__item hcx-row" data-hcx-item data-handle="' + esc(p.handle) + '">' +
        '<a class="hcx-row__img" href="' + esc(p.url) + '" tabindex="-1" aria-hidden="true">' +
          (p.image ? '<img src="' + esc(p.image) + '" alt="" loading="lazy">'
                   : '<span class="hcx-row__ph"></span>') +
        '</a>' +
        '<div class="hcx-row__info">' +
          '<a class="hcx-row__title" href="' + esc(p.url) + '" title="' + esc(p.title) + '">' + esc(p.title) + '</a>' +
          '<span class="hcx-row__price">' +
            '<span class="hcx-row__now">' + esc(p.now) + '</span>' +
            (p.was ? '<s class="hcx-row__was">' + esc(p.was) + '</s>' : '') +
          '</span>' +
          (multi ? '<select class="hcx-row__variant" data-hcx-variant aria-label="' + esc(p.title) + ' size">' + opts + '</select>'
                 : '<input type="hidden" data-hcx-variant value="' + esc(p.variants[0].id) + '">') +
        '</div>' +
        '<button type="button" class="hcx-row__add" data-hcx-add aria-label="Add ' + esc(p.title) + ' to cart">Add</button>' +
      '</li>';
    }

    function render() {
      var items = eligible().slice(0, VISIBLE);
      root.hidden = items.length === 0;
      list.innerHTML = items.map(rowHTML).join('');

      // Running low? Pull in the rest of the catalogue so it never empties.
      if (eligible().length <= VISIBLE && !topUpState.loaded) {
        topUpPool().then(function () {
          var more = eligible().slice(0, VISIBLE);
          if (more.length) {
            root.hidden = false;
            list.innerHTML = more.map(rowHTML).join('');
          }
        });
      }
    }
    root.__hcxRender = render;

    list.addEventListener('change', function (e) {
      var sel = e.target.closest('[data-hcx-variant]');
      if (!sel || sel.tagName !== 'SELECT') return;
      var row = sel.closest('[data-hcx-item]');
      var handle = row && row.getAttribute('data-handle');
      if (!handle) return;

      loadProduct(handle).then(function (p) {
        if (!p) return;
        var v = (p.variants || []).filter(function (x) { return String(x.id) === sel.value; })[0];
        if (!v) return;
        var nowEl = row.querySelector('.hcx-row__now');
        var wasEl = row.querySelector('.hcx-row__was');
        if (nowEl) nowEl.textContent = money(v.price);
        if (v.compare_at_price && v.compare_at_price > v.price) {
          if (wasEl) wasEl.textContent = money(v.compare_at_price);
          else {
            var s = document.createElement('s');
            s.className = 'hcx-row__was';
            s.textContent = money(v.compare_at_price);
            row.querySelector('.hcx-row__price').appendChild(s);
          }
        } else if (wasEl) wasEl.remove();
        var btn = row.querySelector('[data-hcx-add]');
        if (btn) {
          btn.disabled = !v.available;
          btn.textContent = v.available ? 'Add' : 'Sold out';
        }
      });
    });

    list.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-hcx-add]');
      if (!btn || btn.disabled) return;
      e.preventDefault();
      e.stopPropagation();

      var row = btn.closest('[data-hcx-item]');
      var field = row.querySelector('[data-hcx-variant]');
      if (!field || !field.value) return;

      var original = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Adding';
      row.classList.add('is-adding');

      nativeFetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: field.value, quantity: 1 }] })
      })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (data && data.status) throw new Error(data.description || 'add failed');
          row.classList.add('is-leaving');
          syncCart();
        })
        .catch(function () {
          btn.disabled = false;
          btn.textContent = original;
          row.classList.remove('is-adding', 'is-leaving');
        });
    });

    onCartChange(function (cart) {
      inCart = {};
      (cart.items || []).forEach(function (i) { inCart[String(i.variant_id)] = true; });
      render();
    });

    getCart().then(function (cart) {
      lastCart = cart || lastCart;
      inCart = {};
      if (cart) (cart.items || []).forEach(function (i) { inCart[String(i.variant_id)] = true; });
      render();
      // Warm the fallback in the background so there is never a visible gap.
      setTimeout(topUpPool, 1200);
    });
  }

  // ================================================================ boot ===

  function boot() {
    document.querySelectorAll('[data-hc-cart-tiers]').forEach(initTiers);
    document.querySelectorAll('.hc-upsell').forEach(initUpsell);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  var bootTimer = null;
  new MutationObserver(function () {
    clearTimeout(bootTimer);
    bootTimer = setTimeout(boot, 300);
  }).observe(document.body, { childList: true, subtree: true });
})();
